import { Suspense, lazy, useEffect, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeSynchronizer } from "@/components/ThemeSynchronizer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { BloomWidget } from "@/components/BloomWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/SkipToContent";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { queryClient } from "@/config/queryClient";
import { registerServiceWorker } from "@/utils/serviceWorker";
import { useAnalytics } from "@/hooks/useAnalytics";
import { toast } from "sonner";

// Lazy load toast components - not needed for initial render
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load non-critical UI components to reduce initial JS bundle
const CookieConsentBanner = lazy(() => import("@/components/CookieConsent/CookieConsentBanner").then(m => ({ default: m.CookieConsentBanner })));
const InstallPrompt = lazy(() => import("@/components/InstallPrompt").then(m => ({ default: m.InstallPrompt })));

// Eager load only critical landing page
import Landing from "./pages/Landing";
const Register = lazy(() => import("./pages/Register"));

// Lazy load all other public pages for smaller initial bundle
const AboutUnravelYou = lazy(() => import("./pages/AboutUnravelYou"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const CelebrityReadings = lazy(() => import("./pages/CelebrityReadings"));
const MissionGuide = lazy(() => import("./pages/MissionGuide"));
const SignIn = lazy(() => import("./pages/SignIn"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));

// Lazy load protected pages (reduce initial bundle)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const History = lazy(() => import("./pages/History"));
const Reading = lazy(() => import("./pages/Reading"));
const ReadingResult = lazy(() => import("./pages/ReadingResult"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Course = lazy(() => import("./pages/Course"));
const MyInsights = lazy(() => import("./pages/MyInsights"));
const DailyRituals = lazy(() => import("./pages/DailyRituals"));
const KaiAlgorithmPage = lazy(() => import("./pages/KaiAlgorithmPage"));
const PersonalizationDataPage = lazy(() => import("./pages/PersonalizationDataPage"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--gradient-background)' }}>
    <div className="text-center space-y-4">
      <LoadingSpinner size="lg" />
      <p className="text-muted-foreground" aria-live="polite">Loading...</p>
    </div>
  </div>
);

// Analytics wrapper - must be inside BrowserRouter
const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  useAnalytics();
  return <>{children}</>;
};

const AppContent = () => {
  return (
    <BrowserRouter>
      <AnalyticsProvider>
        <SkipToContent />
        <main id="main-content" tabIndex={-1} className="outline-none">
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<Suspense fallback={<LoadingFallback />}><AboutUnravelYou /></Suspense>} />
          <Route path="/get-started" element={<Suspense fallback={<LoadingFallback />}><GetStarted /></Suspense>} />
          <Route path="/celebrity-readings" element={<Suspense fallback={<LoadingFallback />}><CelebrityReadings /></Suspense>} />
          <Route path="/signin" element={<Suspense fallback={<LoadingFallback />}><SignIn /></Suspense>} />
          <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><Register /></Suspense>} />
          <Route path="/verify-email" element={<Suspense fallback={<LoadingFallback />}><VerifyEmail /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<LoadingFallback />}><ResetPassword /></Suspense>} />
          <Route path="/join/:code" element={<Suspense fallback={<LoadingFallback />}><ReferralLanding /></Suspense>} />
          <Route path="/mission-guide" element={<Suspense fallback={<LoadingFallback />}><MissionGuide /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<LoadingFallback />}><Privacy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<LoadingFallback />}><Terms /></Suspense>} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Profile />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <History />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/reading/:readingType" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Reading />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/reading-result/:readingId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <ReadingResult />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/pricing" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Pricing />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/courses/:courseId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Course />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/my-insights" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <MyInsights />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/daily-rituals" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <DailyRituals />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/kai-algorithm" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <KaiAlgorithmPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/personalization" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <PersonalizationDataPage />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* 404 Route */}
          <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
          </Routes>
        </main>
      </AnalyticsProvider>
    </BrowserRouter>
  );
};

const App = () => {
  const [showBloom, setShowBloom] = useState(false);
  const [showNonCritical, setShowNonCritical] = useState(false);

  // Register service worker on mount
  useEffect(() => {
    // Hide loading skeleton when React app mounts
    const skeleton = document.getElementById('app-skeleton');
    if (skeleton) {
      skeleton.style.display = 'none';
    }

    // Defer Bloom widget loading until after initial render
    const bloomTimer = setTimeout(() => {
      setShowBloom(true);
    }, 10000);

    // Defer non-critical UI components (cookie banner, install prompt, toasters)
    const nonCriticalTimer = setTimeout(() => {
      setShowNonCritical(true);
    }, 5000);

    registerServiceWorker({
      onUpdate: () => {
        toast.info('New version available!', {
          description: 'Refresh to get the latest updates.',
          action: {
            label: 'Refresh',
            onClick: () => window.location.reload(),
          },
          duration: 10000,
        });
      },
      onSuccess: () => {
        console.log('[App] Content cached for offline use');
      },
    });

    return () => {
      clearTimeout(bloomTimer);
      clearTimeout(nonCriticalTimer);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen">
          <ThemeSynchronizer />
          {showBloom && <BloomWidget />}
          {showNonCritical && (
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
              <CookieConsentBanner />
              <InstallPrompt />
            </Suspense>
          )}
          <AppContent />
        </div>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
